import { useState, useEffect, useRef, useMemo } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import i18n from "@/app/i18n/i18n";
import { logger } from "@/lib/logger";

type UILanguage = "nl" | "en";

type UseUILanguageArgs = {
  teamId: string;
  uid?: string;
  defaultLang?: UILanguage;
};

type UseUILanguageReturn = {
  lang: UILanguage;
  setLang: (next: UILanguage) => void;
  loading: boolean;
};

export function useUILanguage({
  teamId,
  uid,
  defaultLang = "nl",
}: UseUILanguageArgs): UseUILanguageReturn {
  const storageKey = `ui:lang:${teamId}:${uid || 'anonymous'}`;
  const debouncer = useRef<number | null>(null);

  const [lang, setLangState] = useState<UILanguage>(() => {
    // Try localStorage first for instant boot
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === "nl" || stored === "en") {
        return stored;
      }
    } catch {
      // localStorage not available, use default
    }
    return defaultLang;
  });

  const [loading, setLoading] = useState(true);

  const fsRef = useMemo(() => {
    if (!teamId || !uid) return null;
    // ✅ 4 segments → valid document path
    return doc(db, "teams", teamId, "users", uid);
  }, [teamId, uid]);

  // Load from Firestore on mount
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (!fsRef) {
        setLoading(false);
        return;
      }

      try {
        const snap = await getDoc(fsRef);
        if (!cancelled) {
          if (snap.exists()) {
            const stored = snap.data()?.language;
            if (stored === "nl" || stored === "en") {
              setLangState(stored);
              localStorage.setItem(storageKey, stored);
              void i18n.changeLanguage(stored);
              document.documentElement.lang = stored;
            }
          }
          // If doc doesn't exist, use defaults (already set in state)
        }
      } catch (err: unknown) {
        const error = err as { code?: string; message?: string };
        // Handle permission denied gracefully - use defaults
        if (error.code === 'permission-denied') {
          logger.warn("[useUILanguage] Permission denied - using defaults");
        } else {
          logger.warn("[useUILanguage] Failed to load from Firestore", err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fsRef, storageKey]);

  // Apply language on mount (from initial state)
  useEffect(() => {
    void i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
    
    // Update date locale if using dayjs
    try {
      // @ts-expect-error - dayjs might not be imported globally
      if (typeof dayjs !== "undefined") {
        // @ts-expect-error - dayjs.locale exists if dayjs is defined
        dayjs.locale(lang);
      }
    } catch {
      // dayjs not available, skip locale update
    }
  }, []);

  // Cross-tab sync via storage event
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (!e.key || !e.newValue) return;
      if (e.key === storageKey) {
        const next = e.newValue === "en" ? "en" : "nl";
        if (next !== lang) {
          setLangState(next);
          void i18n.changeLanguage(next);
          document.documentElement.lang = next;
          
          // Update date locale
          try {
            // @ts-expect-error - dayjs might not be imported globally
            if (typeof dayjs !== "undefined") {
              // @ts-expect-error - dayjs.locale exists if dayjs is defined
              dayjs.locale(next);
            }
          } catch {
            // dayjs not available, skip locale update
          }
        }
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [storageKey, lang]);

  const setLang = (next: UILanguage) => {
    if (next !== "nl" && next !== "en") return;

    // Update state immediately
    setLangState(next);

    // Mirror to localStorage
    localStorage.setItem(storageKey, next);

    // Update i18n and HTML lang
    void i18n.changeLanguage(next);
    document.documentElement.lang = next;

    // Update date locale if using dayjs
    try {
      // @ts-expect-error - dayjs might not be imported globally
      if (typeof dayjs !== "undefined") {
        // @ts-expect-error - dayjs.locale exists if dayjs is defined
        dayjs.locale(next);
      }
    } catch {
      // dayjs not available, skip locale update
    }

    // Debounced write to Firestore (300ms)
    if (debouncer.current) {
      window.clearTimeout(debouncer.current);
    }

    debouncer.current = window.setTimeout(async () => {
      if (!fsRef) return;
      try {
        await setDoc(fsRef, { language: next }, { merge: true });
      } catch (err: unknown) {
        const error = err as { code?: string; message?: string };
        // Handle permission denied with user-friendly message
        if (error.code === 'permission-denied') {
          logger.warn("[useUILanguage] Permission denied - cannot save language preference");
          // Could show a toast here if needed
        } else {
          logger.warn("[useUILanguage] Failed to persist to Firestore", err);
        }
      }
    }, 300);
  };

  return { lang, setLang, loading };
}
